import { requestGet, requestPost } from '@/utils/request';

export const BET_SEAL_SECONDS = 30;

const api = {
    HOME: '/user/home',
    CHANGE_PASSWORD: '/user/changePassword',
    BET_LIST: '/user/bet/list',
    BET_SUBMIT: '/user/bet/submit',
    FLOW_LIST: '/user/flow/list',
    REPORT_LIST: '/user/report/list',
    LOTTO_LIST: '/lottery/lottoList',
    TREND_LIST: '/lottery/trendList',
    PREDICT_LIST: '/lottery/predictList',
};

const WIN_LOSE_MAP = {
    WIN: 'win',
    LOST: 'lose',
    WAIT: 'pending',
};

const WIN_LOSE_TEXT = {
    WIN: '赢',
    LOST: '输',
    WAIT: '待开奖',
};

const SUBJECT_TEXT = {
    1: '充值',
    2: '提现',
};

const REF_TYPE_TEXT = {
    recharge: '充值',
    cashout: '提现',
    bet: '下注',
    bet_settle: '中奖',
    cashout_refund: '提现退回',
    member_topup: '上分',
    member_cashout: '下分',
    member_cash_ref: '驳回',
};

export function parseTreatedContent(treatedContent, dianshu) {
    const text = String(treatedContent || '').trim();
    const match = text.match(/^(\d+)\+(\d+)\+(\d+)=(\d+)$/);
    if (match) {
        return {
            nums: [Number(match[1]), Number(match[2]), Number(match[3])],
            sum: Number(match[4]),
        };
    }
    return {
        nums: [0, 0, 0],
        sum: Number(dianshu) || 0,
    };
}

function buildTagsFromLotto(lotto) {
    if (!lotto) {
        return [];
    }
    const tags = [];
    if (lotto.da === 'Y') tags.push('大');
    if (lotto.xiao === 'Y') tags.push('小');
    if (lotto.dan === 'Y') tags.push('单');
    if (lotto.shuang === 'Y') tags.push('双');
    return tags;
}

function normalizeLastLotto(raw) {
    if (!raw) {
        return null;
    }
    return {
        id: raw.id,
        issue: raw.issue,
        treatedContent: raw.treatedContent ?? raw.treated_content ?? '',
        dianshu: Number(raw.dianshu) || 0,
        da: raw.da,
        xiao: raw.xiao,
        dan: raw.dan,
        shuang: raw.shuang,
    };
}

function normalizeCurrentLotto(raw) {
    if (!raw) {
        return null;
    }
    return {
        id: raw.id,
        issue: raw.issue,
        unveilTime: Number(raw.unveilTime ?? raw.unveil_time) || 0,
    };
}

export function isEmptyLastDraw(lastDraw) {
    if (!lastDraw || !Array.isArray(lastDraw.nums)) {
        return true;
    }
    if (Array.isArray(lastDraw.tags) && lastDraw.tags.length > 0) {
        return false;
    }
    return lastDraw.nums.every((num) => Number(num) === 0);
}

export function mergeLastDraw(prev, next, prevIssueNo, nextIssueNo) {
    if (!next) {
        return prev;
    }
    if (!prev) {
        return next;
    }
    if (
        nextIssueNo
        && prevIssueNo === nextIssueNo
        && isEmptyLastDraw(next)
        && !isEmptyLastDraw(prev)
    ) {
        return prev;
    }
    return next;
}

/** 与后端 LottoBetModel::lookupIdents 保持一致 */
export function resolveBetLookupIdents(storeIdent) {
    const ident = String(storeIdent || '').trim();
    if (!ident) {
        return [];
    }

    if (/^\d+$/.test(ident)) {
        const point = parseInt(ident, 10);
        if (point < 0 || point > 27) {
            return [];
        }
        return [`he${point}`];
    }

    if (/^(bai|shi|ge)\d+$/.test(ident)) {
        return [ident];
    }

    return [ident];
}

export function resolveBetOdds(betConfig, storeIdent) {
    const config = betConfig || {};
    const lookupIdents = resolveBetLookupIdents(storeIdent);
    for (let i = 0; i < lookupIdents.length; i += 1) {
        const item = config[lookupIdents[i]];
        if (item && item.odds > 0) {
            return item.odds;
        }
    }
    return 0;
}

export function resolveBetLimits(betConfig, storeIdent, fallback = { min: 0, max: 0 }) {
    const config = betConfig || {};
    const lookupIdents = resolveBetLookupIdents(storeIdent);
    for (let i = 0; i < lookupIdents.length; i += 1) {
        const item = config[lookupIdents[i]];
        if (item) {
            return {
                min: Number(item.min) || 0,
                max: Number(item.max) || 0,
            };
        }
    }
    return fallback;
}

export function normalizeBetConfigMap(raw) {
    if (!raw || typeof raw !== 'object') {
        return {};
    }

    const config = {};
    Object.keys(raw).forEach((ident) => {
        const item = raw[ident];
        if (!item || typeof item !== 'object') {
            return;
        }
        const odds = Number(item.odds);
        const min = Number(item.min);
        const max = Number(item.max);
        config[ident] = {
            odds: !Number.isNaN(odds) && odds > 0 ? odds : 0,
            min: !Number.isNaN(min) && min > 0 ? min : 0,
            max: !Number.isNaN(max) && max > 0 ? max : 0,
        };
    });

    return config;
}

export function normalizeHomeData(data) {
    const lastLotto = normalizeLastLotto(data.lastLotto || data.last_lotto || null);
    const currentLotto = normalizeCurrentLotto(data.currentLotto || data.current_lotto || null);
    const parsed = lastLotto
        ? parseTreatedContent(lastLotto.treatedContent, lastLotto.dianshu)
        : { nums: [0, 0, 0], sum: 0 };

    return {
        balance: data.balance != null ? Number(data.balance) : 0,
        lastIssueNo: lastLotto ? String(lastLotto.issue) : '',
        nextIssueNo: currentLotto ? String(currentLotto.issue) : '',
        lastDraw: {
            nums: parsed.nums,
            sum: parsed.sum,
            tags: buildTagsFromLotto(lastLotto),
        },
        unveilTime: Number(data.unveilTime ?? data.unveil_time) || 0,
        countdownSeconds: Number(data.countdown) || 0,
        isMaintenance: Boolean(data.isMaintenance ?? data.is_maintenance),
        countdownPhase: data.countdownPhase || data.countdown_phase || 'betting',
        betConfig: normalizeBetConfigMap(data.betConfig || data.bet_config || {}),
        role: Number(data.role) || 5,
        parentId: Number(data.parentId) || 0,
        userId: String(data.id || data.userId || data.user_id || ''),
        nickname: data.nickname || '',
        loginAccount: data.loginAccount || data.login_account || '',
        roleLabel: data.roleLabel || data.role_label || '',
        isSpecial: (data.isSpecial || data.is_special || 'N') === 'Y',
        roleLevel: Number(data.roleLevel ?? data.role_level) || 0,
    };
}

export function formatBetDisplayName(ident, fallbackName = '') {
    const key = String(ident || '').trim();
    if (!key) {
        return String(fallbackName || '').trim();
    }

    const IDENT_LABEL_MAP = {
        da: '大',
        xiao: '小',
        dan: '单',
        shuang: '双',
        xiaodan: '小单',
        xiaoshuang: '小双',
        dadan: '大单',
        dashuang: '大双',
        jixiao: '极小',
        jida: '极大',
        shunzi: '顺',
        shuanzi: '顺',
        baozi: '豹',
        duizi: '对',
    };
    const POSITION_LABEL_MAP = { bai: '百', shi: '十', ge: '个' };

    if (/^\d+$/.test(key)) {
        return `和单点 ${key}`;
    }

    const posDxdsMatch = key.match(/^(bai|shi|ge)(da|xiao|dan|shuang)$/);
    if (posDxdsMatch) {
        const position = POSITION_LABEL_MAP[posDxdsMatch[1]] || posDxdsMatch[1];
        const option = IDENT_LABEL_MAP[posDxdsMatch[2]] || posDxdsMatch[2];
        return `${position}${option}`;
    }

    const posMatch = key.match(/^(bai|shi|ge)(\d+)$/);
    if (posMatch) {
        const position = POSITION_LABEL_MAP[posMatch[1]] || posMatch[1];
        return `${position}单点 ${posMatch[2]}`;
    }

    const name = String(fallbackName || '').trim();
    if (name) {
        return name;
    }

    return IDENT_LABEL_MAP[key] || key;
}

export function normalizeBetRow(row) {
    const betName = formatBetDisplayName(
        row.lottoBetIdent || row.lotto_bet_ident,
        row.lottoBetName || row.lotto_bet_name,
    );
    const amount = row.betAmount != null ? row.betAmount : '';
    const winLose = row.winLose || 'WAIT';
    const status = WIN_LOSE_MAP[winLose] || 'pending';
    const changeAmount = row.changeAmount != null ? Number(row.changeAmount) : 0;

    let profit = '0';
    if (winLose === 'WIN') {
        const betNum = Number(amount) || 0;
        const payout = betNum + changeAmount;
        if (payout > 0) {
            const payoutText = Number.isInteger(payout) ? String(payout) : payout.toFixed(2);
            profit = `+${payoutText}`;
        } else {
            profit = changeAmount > 0 ? `+${changeAmount}` : String(changeAmount);
        }
    } else if (winLose === 'LOST') {
        profit = changeAmount < 0 ? String(changeAmount) : `-${amount}`;
    }

    return {
        id: String(row.id || ''),
        time: row.created || '',
        nickname: row.nickname || '',
        loginAccount: row.loginAccount || row.login_account || '',
        userName: row.nickname || row.loginAccount || row.login_account || '',
        betText: `${betName} ${amount}`.trim(),
        resultText: WIN_LOSE_TEXT[winLose] || '待开奖',
        status,
        issueNo: row.lottoIssue != null ? String(row.lottoIssue) : '',
        sourceData: row.lottoContent || row.lotto_content || '',
        drawResult: row.lottoTreatedContent || '-',
        betType: betName,
        amount: String(amount),
        profit,
        odds: row.odds,
        winLose,
        lottoId: row.lottoId,
        lottoBetIdent: row.lottoBetIdent,
        changeAmount,
    };
}

export function normalizeFlowRow(row) {
    const subject = Number(row.subject);
    const refType = row.refType || row.ref_type || '';
    const refNickname = (row.refNickname || row.ref_nickname || '').trim();
    const amount = Number(row.amount);
    const flowTitle = (row.flowTitle || row.flow_title || '').trim();
    let title = flowTitle;

    if (!title && refType === 'member_topup' && refNickname) {
        title = amount < 0 ? `给${refNickname}上分` : `收到${refNickname}上分`;
    }
    if (!title && refType === 'member_cashout' && refNickname) {
        title = amount < 0 ? `向${refNickname}下分` : `收到${refNickname}下分`;
    }
    if (!title && refType === 'member_cash_ref' && refNickname) {
        title = `收到${refNickname}驳回`;
    }
    if (!title) {
        title = REF_TYPE_TEXT[refType] || SUBJECT_TEXT[subject] || refType || '账户变动';
    }

    return {
        id: String(row.id || ''),
        time: row.created || '',
        created: row.created || '',
        title,
        amount,
        afterAmount: row.afterAmount ?? row.after_amount,
        subject,
        refType,
        refId: row.refId ?? row.ref_id,
        refNickname,
    };
}

export function normalizeLottoRow(row) {
    const parsed = parseTreatedContent(row.treatedContent, row.dianshu);
    return {
        id: String(row.id || ''),
        issueNo: String(row.issue || ''),
        time: row.created || '',
        nums: parsed.nums,
        sum: parsed.sum,
        treatedContent: row.treatedContent || '',
        dianshu: row.dianshu,
        unveilTime: row.unveilTime,
    };
}

export function normalizeTrendRow(row) {
    const tags = row.tags || {};
    const issueNo = String(row.issue || row.issueNo || '');
    const nums = row.nums || [0, 0, 0];
    const value = Number(row.dianshu ?? row.value) || 0;
    const valueText = row.valueText || row.value_text
        || (nums.length === 3 ? `${nums[0]}+${nums[1]}+${nums[2]}=${value}` : String(value));

    return {
        id: String(row.id || issueNo),
        issueNo,
        value,
        valueText,
        nums,
        tags: {
            baiBig: tags.baiBig || tags.bai_big || '',
            baiSmall: tags.baiSmall || tags.bai_small || '',
            baiOdd: tags.baiOdd || tags.bai_odd || '',
            baiEven: tags.baiEven || tags.bai_even || '',
            shiBig: tags.shiBig || tags.shi_big || '',
            shiSmall: tags.shiSmall || tags.shi_small || '',
            shiOdd: tags.shiOdd || tags.shi_odd || '',
            shiEven: tags.shiEven || tags.shi_even || '',
            geBig: tags.geBig || tags.ge_big || '',
            geSmall: tags.geSmall || tags.ge_small || '',
            geOdd: tags.geOdd || tags.ge_odd || '',
            geEven: tags.geEven || tags.ge_even || '',
            big: tags.big || '',
            small: tags.small || '',
            odd: tags.odd || '',
            even: tags.even || '',
            bigOdd: tags.bigOdd || tags.big_odd || '',
            bigEven: tags.bigEven || tags.big_even || '',
            smallOdd: tags.smallOdd || tags.small_odd || '',
            smallEven: tags.smallEven || tags.small_even || '',
            straight: tags.straight || '',
            leopard: tags.leopard || '',
            pair: tags.pair || '',
        },
    };
}

export function fetchHomeData() {
    return requestGet(api.HOME).then((res) => {
        if (!res.isSucceed) {
            return res;
        }
        return {
            ...res,
            data: normalizeHomeData(res.data || {}),
        };
    });
}

export function fetchBetRecords(page = 1, pageSize = 10, keyword = '', scope = '') {
    const params = { page, pageSize };
    if (keyword) {
        params.keyword = keyword;
    }
    if (scope) {
        params.scope = scope;
    }
    return requestGet(api.BET_LIST, params).then((res) => {
        if (!res.isSucceed) {
            return res;
        }
        const rows = (res.data.recordList || []).map((row) => normalizeBetRow(row));
        return {
            ...res,
            data: { recordList: rows, list: rows },
        };
    });
}

export function fetchBetRecordList(page = 1, pageSize = 20, keyword = '') {
    return fetchBetRecords(page, pageSize, keyword, 'self');
}

export function fetchLottoList(page = 1, pageSize = 20) {
    return requestGet(api.LOTTO_LIST, { page, pageSize }).then((res) => {
        if (!res.isSucceed) {
            return res;
        }
        const rows = (res.data.recordList || []).map((row) => normalizeLottoRow(row));
        return {
            ...res,
            data: { recordList: rows, list: rows },
        };
    });
}

export function fetchTrendList(page = 1, pageSize = 20) {
    return requestGet(api.TREND_LIST, { page, pageSize }).then((res) => {
        if (!res.isSucceed) {
            return res;
        }
        const rows = (res.data.recordList || []).map((row) => normalizeTrendRow(row));
        return {
            ...res,
            data: { recordList: rows, list: rows },
        };
    });
}

export function fetchPredictList(page = 1, pageSize = 20) {
    return requestGet(api.PREDICT_LIST, { page, pageSize }).then((res) => {
        if (!res.isSucceed) {
            return res;
        }
        const rows = (res.data.recordList || []).map((row) => normalizePredictRow(row));
        return {
            ...res,
            data: { recordList: rows, list: rows },
        };
    });
}

export function normalizePredictRow(row) {
    const isPending = Boolean(row.isPending);
    return {
        id: String(row.id || ''),
        issueNo: String(row.issueNo || row.issue || ''),
        time: row.time || '',
        nums: Array.isArray(row.nums) ? row.nums : [],
        sum: row.sum != null && row.sum !== '' ? row.sum : '',
        predictText: row.predictText || '',
        isPending,
        isCorrect: isPending ? null : Boolean(row.isCorrect),
    };
}

export function flowList(page = 1) {
    return requestGet(api.FLOW_LIST, { page }).then((res) => {
        if (!res.isSucceed) {
            return res;
        }
        const rows = (res.data.recordList || []).map((row) => normalizeFlowRow(row));
        return {
            ...res,
            data: { recordList: rows, list: rows },
        };
    });
}

export function changePassword(payload) {
    return requestPost(api.CHANGE_PASSWORD, payload);
}

export function submitBet(payload) {
    return requestPost(api.BET_SUBMIT, {
        items: payload.items || [],
        mipai: payload.mipai === 'yes' ? 'Y' : 'N',
    });
}

function normalizeReportRow(row) {
    return {
        id: row.id || `${row.date || ''}_${row.userId || row.user_id || ''}`,
        date: row.date || '',
        userId: String(row.userId || row.user_id || ''),
        nickname: row.nickname || '',
        loginAccount: row.loginAccount || row.login_account || '',
        userText: row.userText || row.user_text || '',
        washVolume: row.washVolume != null ? row.washVolume : row.wash_volume,
        winLose: row.winLose != null ? row.winLose : row.win_lose,
    };
}

export function fetchReportList(params = {}) {
    const {
        page = 1,
        pageSize = 20,
        keyword = '',
        dateStart = '',
        dateEnd = '',
    } = params;

    return requestGet(api.REPORT_LIST, {
        page,
        pageSize,
        keyword,
        dateStart,
        dateEnd,
    }).then((res) => {
        if (!res.isSucceed) {
            return res;
        }

        const summaryRaw = res.data.summary || {};
        const rows = (res.data.recordList || res.data.record_list || []).map((row) => normalizeReportRow(row));

        return {
            ...res,
            data: {
                summary: {
                    washVolume: summaryRaw.washVolume != null
                        ? summaryRaw.washVolume
                        : summaryRaw.wash_volume,
                    winLose: summaryRaw.winLose != null
                        ? summaryRaw.winLose
                        : summaryRaw.win_lose,
                },
                list: rows,
                recordList: rows,
            },
        };
    });
}
